<template>
  <div class="deploy-section">
    <div class="grid-layout">
      <!-- Left: pick the target dock -->
      <div class="form-card">
        <div class="card-header">
          <div class="header-icon">
            <Icon name="search" :size="20" color="#1b92a6" />
          </div>
          <div>
            <h3 class="card-title">Target dock</h3>
            <p class="card-subtitle">Pick the organisation, then the dock to redeploy</p>
          </div>
        </div>

        <div class="form-grid">
          <label class="select-label">Organisation</label>
          <SelectInput
            v-model="selectedOrgId"
            :options="orgOptions"
            :label="orgsQuery.isPending.value ? 'Loading organisations…' : 'Choose an organisation'"
            :disabled="orgsQuery.isPending.value || isDeploying"
            :is-searchable="true"
            max-height="260px"
          />
          <p v-if="orgsQuery.isError.value" class="error_message">
            Couldn't load organisations.
            <button type="button" class="retry-link" @click="orgsQuery.refetch()">Retry</button>
          </p>

          <div class="lookup-toggle">
            <button
              type="button"
              class="lookup-toggle-btn"
              :data-active="pickMode === 'list' || null"
              :disabled="isDeploying"
              @click="pickMode = 'list'"
            >
              Choose from list
            </button>
            <button
              type="button"
              class="lookup-toggle-btn"
              :data-active="pickMode === 'manual' || null"
              :disabled="isDeploying"
              @click="pickMode = 'manual'"
              title="Type a deviceID directly. Handy when the dock is not in the list above."
            >
              Enter device ID
            </button>
          </div>

          <template v-if="pickMode === 'list'">
            <label class="select-label">Dock</label>
            <SelectInput
              v-model="selectedDeviceId"
              :options="dockOptions"
              :label="dockSelectLabel"
              :disabled="!selectedOrgId || orgDevicesQuery.isPending.value || isDeploying"
              :is-searchable="true"
              max-height="300px"
            />
            <p v-if="orgDevicesQuery.isError.value" class="error_message">
              Couldn't load devices for this organisation.
              <button type="button" class="retry-link" @click="orgDevicesQuery.refetch()">
                Retry
              </button>
            </p>
          </template>
          <template v-else>
            <NumericInput
              type="text"
              label="deviceID"
              :value="manualDeviceId"
              placeholder="e.g. 86021229"
              :editable="!isDeploying"
              @update:value="(v: string) => (manualDeviceId = v)"
            />
            <p v-if="manualDeviceId.trim() && !targetDevice" class="warn-banner mt-0">
              This deviceID is not in the selected organisation's device list. The deploy will
              assume dock type <code>{{ FALLBACK_DOCK_TYPE }}</code> and the selected organisation.
            </p>
          </template>
        </div>

        <!-- Currently saved code -->
        <div v-if="targetDeviceId" class="fields-section">
          <div class="fields-header">
            <h4 class="fields-title">Currently saved deploy code</h4>
            <p class="fields-subtitle">
              What <code>{{ targetDeviceId }}</code> auto-deploys with today. Deploying below
              overwrites it.
            </p>
          </div>
          <div v-if="savedCodeLoading" class="empty-match">
            <Loader />
            <p>Fetching saved code…</p>
          </div>
          <div v-else-if="savedCode" class="match-panel">
            <div class="match-row">
              <span class="match-label">machine</span>
              <span class="match-value mono">{{ savedCode.machine || '—' }}</span>
            </div>
            <div class="match-row">
              <span class="match-label">mqtt host</span>
              <span class="match-value mono">{{ savedCode.mqttHost || '—' }}</span>
            </div>
            <div class="match-row">
              <span class="match-label">mqtt port</span>
              <span class="match-value mono">{{ savedCode.mqttPort || '—' }}</span>
            </div>
            <div class="match-row">
              <span class="match-label">gateway SN</span>
              <span class="match-value mono">{{ savedCode.gatewaySN || '—' }}</span>
            </div>
            <div class="match-row">
              <span class="match-label">compose up</span>
              <span class="match-value">{{ savedCode.composeup ? 'yes' : 'no' }}</span>
            </div>
          </div>
          <div v-else class="empty-match">
            <Icon name="info_circle" :size="20" color="var(--txt-secondary-1)" />
            <p>No deploy code saved for this dock yet.</p>
          </div>
        </div>
      </div>

      <!-- Right: new code + deploy -->
      <div class="form-card">
        <div class="card-header">
          <div class="header-icon">
            <Icon name="settings" :size="20" color="#1b92a6" />
          </div>
          <div>
            <h3 class="card-title">Deploy with a new code</h3>
            <p class="card-subtitle">
              Validates the code, saves it for the dock, then dispatches the deploy
            </p>
          </div>
        </div>

        <div v-if="!targetDeviceId" class="empty-match">
          <Icon name="info_circle" :size="20" color="var(--txt-secondary-1)" />
          <p>Pick a dock on the left to start.</p>
        </div>

        <template v-else-if="showProgress && deployingDeviceId">
          <div class="match-panel">
            <div class="match-row">
              <span class="match-label">deviceID</span>
              <span class="match-value mono">{{ deployingDeviceId }}</span>
            </div>
            <div class="match-row">
              <span class="match-label">dock type</span>
              <span class="match-value">{{ deployingDeviceType }}</span>
            </div>
          </div>
          <DeployPanel
            :target-name="deployingDeviceId"
            :current-stage="deployEntry?.currentStage ?? 0"
            :current-message="deployEntry?.currentMessage"
            :raw-steps="deployEntry?.steps || []"
            :failed="deployEntry?.status === 'failed'"
            :error-reason="deployEntry?.errorReason"
            :started-at="deployEntry?.startedAt"
          />
          <p v-if="actionError" class="error_message">{{ actionError }}</p>
          <div class="form-actions">
            <button class="generate-btn" type="button" :disabled="!isTerminal" @click="resetForm">
              <span>{{ isTerminal ? 'Deploy another dock' : 'Deploying…' }}</span>
            </button>
            <button v-if="!isTerminal" class="clear-btn" type="button" @click="runInBackground">
              Run in background
            </button>
          </div>
        </template>

        <template v-else>
          <div class="match-panel">
            <div class="match-row">
              <span class="match-label">deviceID</span>
              <span class="match-value mono">{{ targetDeviceId }}</span>
            </div>
            <div class="match-row">
              <span class="match-label">dock type</span>
              <span class="match-value">{{ targetDeviceType }}</span>
            </div>
            <div class="match-row">
              <span class="match-label">device name</span>
              <span class="match-value">{{ targetDevice?.deviceName || '—' }}</span>
            </div>
            <div class="match-row">
              <span class="match-label">device group</span>
              <span class="match-value">{{ targetDevice?.deviceGroupName || '—' }}</span>
            </div>
            <div class="match-row">
              <span class="match-label">organisation</span>
              <span class="match-value mono">{{ targetOrganisationId }}</span>
            </div>
          </div>

          <div class="form-grid mt-4">
            <NumericInput
              type="text"
              label="New deploy code"
              :value="codeInput"
              placeholder="Paste the deploy code here"
              :editable="!isDeploying"
              @update:value="(v: string) => (codeInput = v)"
            />
          </div>

          <p v-if="actionError" class="error_message mb-2">{{ actionError }}</p>

          <div class="form-actions">
            <button
              class="generate-btn"
              type="button"
              :disabled="!canDeploy || isDeploying"
              @click="saveAndDeploy"
            >
              <Loader v-if="isDeploying" />
              <span v-else>Save code and deploy</span>
            </button>
            <button class="clear-btn" type="button" :disabled="isDeploying" @click="resetForm">
              Clear
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import useOnboardingDevices from '@/composables/Device/useOnboardingDevices'
import useOrganisations from '@/composables/useOrganisations'
import useRequests from '@/composables/useRequests'
import useDockDeploy from '@/composables/Device/useDockDeploy'
import { validateMqttHostForDock } from '@/composables/Device/deployPayload'
import { appStore } from '@/store/app'
import { ADMIN_ORGANISATION_ID } from '@/constants/appData'
import { dockTypes } from '@/constants/deviceManager'
import { uuid } from '@/helpers'
import log from '@/utils/useLogger'
import decrypt from '@/utils/decrypt'
import Icon from '@/components/Icon.vue'
import Loader from '@/components/ui/Loader.vue'
import NumericInput from '@/components/Inputs/NumericInput.vue'
import SelectInput from '@/components/BaseComponents/SelectInput.vue'
import DeployPanel from '@/components/onboarding/DeployPanel.vue'
import type { OnboardingDevice } from '@/types/onboarding.types'
import type { deployData } from '@/types/devices.types'

// Manual deviceID entry has no row to read the type from. Every legacy
// dock that still relies on a pasted deploy code is a DJI Dock 3.
const FALLBACK_DOCK_TYPE = 'DJI_DOCK_3'

const { addNotifications } = appStore()
const { useOrganisationsQuery } = useOrganisations()
const { useOrgDevicesQuery } = useOnboardingDevices()
const { saveDeployCode, fetchDeployData } = useRequests()
const { mutateAsync: persistDeployCode } = saveDeployCode()
const { resolveRcId, buildDeployPayload, dispatchDeploy, entryFor } = useDockDeploy()

// ---- Target selection -------------------------------------------------

const actionError = ref('')

const orgsQuery = useOrganisationsQuery()
const selectedOrgId = ref<string>(ADMIN_ORGANISATION_ID)
const orgDevicesQuery = useOrgDevicesQuery(selectedOrgId)

type PickMode = 'list' | 'manual'
const pickMode = ref<PickMode>('list')
const selectedDeviceId = ref<string>('')
const manualDeviceId = ref('')

const orgOptions = computed(() =>
  (orgsQuery.data.value ?? []).map((o) => ({
    value: o.organisationID,
    label: `${o.organisationName || '(unnamed)'} — ${o.organisationID}`,
  })),
)

const orgDocks = computed<OnboardingDevice[]>(() =>
  (orgDevicesQuery.data.value ?? []).filter((d) => dockTypes.includes(d.deviceType)),
)

// Label carries name, group and ID so the searchable select matches on
// whichever one the admin remembers.
const dockOptions = computed(() =>
  orgDocks.value.map((d) => {
    const parts = [d.deviceName || d.deviceID]
    if (d.deviceGroupName) parts.push(d.deviceGroupName)
    parts.push(d.deviceID)
    return { value: d.deviceID, label: parts.join(' · ') }
  }),
)

const dockSelectLabel = computed(() => {
  if (!selectedOrgId.value) return 'Choose an organisation first'
  if (orgDevicesQuery.isPending.value) return 'Loading docks…'
  if (!orgDocks.value.length) return 'No docks in this organisation'
  return 'Choose a dock'
})

const targetDeviceId = computed(() =>
  pickMode.value === 'list' ? selectedDeviceId.value : manualDeviceId.value.trim(),
)

const targetDevice = computed<OnboardingDevice | null>(() => {
  if (!targetDeviceId.value) return null
  return (orgDevicesQuery.data.value ?? []).find((d) => d.deviceID === targetDeviceId.value) ?? null
})

const targetDeviceType = computed(() => targetDevice.value?.deviceType || FALLBACK_DOCK_TYPE)
const targetOrganisationId = computed(
  () => targetDevice.value?.organisationID || selectedOrgId.value,
)

// Changing org or pick mode invalidates the current dock choice.
watch(selectedOrgId, () => {
  selectedDeviceId.value = ''
  manualDeviceId.value = ''
})
watch(pickMode, () => {
  selectedDeviceId.value = ''
  manualDeviceId.value = ''
})

// ---- Currently saved code ---------------------------------------------

const savedCode = ref<deployData | null>(null)
const savedCodeLoading = ref(false)

const loadSavedCode = async (deviceId: string) => {
  savedCode.value = null
  if (!deviceId) return
  savedCodeLoading.value = true
  try {
    const data = await fetchDeployData(deviceId)
    // Guard against a stale response landing after the admin picked
    // another dock while this request was in flight.
    if (targetDeviceId.value === deviceId) savedCode.value = data
  } finally {
    if (targetDeviceId.value === deviceId) savedCodeLoading.value = false
  }
}

let savedCodeTimer: ReturnType<typeof setTimeout> | null = null
watch(targetDeviceId, (id) => {
  actionError.value = ''
  if (savedCodeTimer) clearTimeout(savedCodeTimer)
  if (!id) {
    savedCode.value = null
    savedCodeLoading.value = false
    return
  }
  // Manual entry fires per keystroke; debounce so we don't hammer the
  // deploy-data endpoint with partial IDs.
  const delay = pickMode.value === 'manual' ? 500 : 0
  savedCodeTimer = setTimeout(() => loadSavedCode(id), delay)
})

// ---- Deploy -----------------------------------------------------------

const codeInput = ref('')
const isDeploying = ref(false)
const showProgress = ref(false)
const deployingDeviceId = ref<string>('')
const deployingDeviceType = ref<string>('')

const deployEntry = computed(() => entryFor(deployingDeviceId.value || null).value)
const isTerminal = computed(
  () => deployEntry.value?.status === 'success' || deployEntry.value?.status === 'failed',
)

const canDeploy = computed(() => !!targetDeviceId.value && !!codeInput.value.trim())

const decryptCode = async (code: string): Promise<deployData | null> => {
  try {
    const decrypted = await decrypt(code)
    if (!decrypted) {
      actionError.value = 'Invalid deploy code: it could not be decrypted.'
      return null
    }
    const parsed = JSON.parse(decrypted) as Partial<deployData>
    if (!parsed.machine || !parsed.mqttHost) {
      actionError.value = 'Invalid deploy code: missing machine or MQTT host.'
      return null
    }
    return {
      machine: parsed.machine,
      mqttHost: parsed.mqttHost,
      mqttPort: parsed.mqttPort ?? '',
      composeup: !!parsed.composeup,
      gatewaySN: parsed.gatewaySN ?? '',
    }
  } catch (error) {
    log.error('Admin deploy: error decrypting code', error)
    actionError.value = `Invalid deploy code: ${error instanceof Error ? error.message : 'unknown error'}`
    return null
  }
}

/**
 * Order matters and differs from the legacy modal on purpose:
 *   1. decrypt and validate (nothing persisted if the code is bad)
 *   2. save the code (POST /deploy-data/code overwrites the old one)
 *   3. dispatch the deploy
 * The new code is the source of truth even if the dispatch fails, so it
 * is saved before we talk to GitHub.
 */
const saveAndDeploy = async () => {
  if (!canDeploy.value || isDeploying.value) return
  const deviceId = targetDeviceId.value
  const deviceType = targetDeviceType.value
  const organisationId = targetOrganisationId.value
  const code = codeInput.value.trim()

  isDeploying.value = true
  actionError.value = ''
  try {
    const data = await decryptCode(code)
    if (!data) return

    try {
      await persistDeployCode({ code, deviceId })
    } catch (error: any) {
      log.error('Admin deploy: failed to save deploy code', error)
      actionError.value =
        error?.response?.data?.error || error?.message || 'Failed to save the deploy code.'
      return
    }
    savedCode.value = data

    const device = {
      deviceID: deviceId,
      deviceType,
      deviceGroupID: targetDevice.value?.deviceGroupID,
    }
    // FLY3D-1085 — a pasted code can carry a stale broker host. The
    // workflow only format-checks it, so a wrong-but-well-formed IP
    // crash-loops the dock; cross-check it against the dock's record
    // before anything is dispatched.
    const mqttError = validateMqttHostForDock(data.mqttHost, {
      deviceID: deviceId,
      mqtt_host: targetDevice.value?.mqtt_host,
    })
    if (mqttError) {
      actionError.value = mqttError
      return
    }

    const payload = buildDeployPayload(device, data, {
      organisationId,
      rcId: resolveRcId(device, orgDevicesQuery.data.value ?? []),
    })

    deployingDeviceId.value = deviceId
    deployingDeviceType.value = deviceType
    showProgress.value = true
    const { success, error } = await dispatchDeploy(deviceId, payload)
    if (!success) {
      showProgress.value = false
      actionError.value =
        error || 'Deploy code saved, but the deploy could not be dispatched. Please try again.'
      addNotifications({
        id: uuid(10),
        notification: `Failed to dispatch deploy for ${deviceId}`,
        type: 'error',
      })
      return
    }
    addNotifications({
      id: uuid(10),
      notification: `New deploy code saved and deploy started for ${deviceId}`,
      type: 'success',
    })
    codeInput.value = ''
  } catch (error) {
    log.error('Admin deploy: unexpected error', error)
    actionError.value = `Failed to deploy dock: ${error instanceof Error ? error.message : 'unknown error'}`
    showProgress.value = false
  } finally {
    isDeploying.value = false
  }
}

// The socket is module-scoped, so leaving the progress view does not
// cancel the deploy. The device-management page keeps tracking it.
const runInBackground = () => {
  showProgress.value = false
  deployingDeviceId.value = ''
  deployingDeviceType.value = ''
}

const resetForm = () => {
  runInBackground()
  codeInput.value = ''
  actionError.value = ''
  selectedDeviceId.value = ''
  manualDeviceId.value = ''
}
</script>

<style scoped>
.deploy-section {
  padding-bottom: 2rem;
}

.grid-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

@media (max-width: 1200px) {
  .grid-layout {
    grid-template-columns: 1fr;
  }
}

.form-card {
  background: linear-gradient(145deg, var(--idi-shade0-100) 0%, rgba(11, 14, 18, 0.95) 100%);
  border: 1px solid var(--idi-shade3-50);
  border-radius: var(--rad-xl);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
}
.header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(27, 146, 166, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.card-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--txt-primary);
  margin: 0;
}
.card-subtitle {
  font-size: 0.75rem;
  color: var(--txt-secondary-1);
  margin: 2px 0 0;
}

.form-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.lookup-toggle {
  display: flex;
  background: var(--idi-shade2-100);
  border: 1px solid var(--idi-shade3-50);
  border-radius: var(--rad-lg);
  padding: 4px;
  gap: 4px;
}
.lookup-toggle-btn {
  flex: 1;
  background: transparent;
  border: none;
  color: var(--txt-secondary-1);
  font-size: 0.8125rem;
  font-weight: 600;
  padding: 8px 12px;
  border-radius: var(--rad-m);
  cursor: pointer;
  font-family: var(--primary-font);
  transition: all 0.15s;
}
.lookup-toggle-btn:hover {
  color: var(--txt-primary);
}
.lookup-toggle-btn[data-active] {
  background: var(--idi-shade3-50);
  color: var(--txt-primary);
}
.lookup-toggle-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 4px;
}
.generate-btn {
  flex: 1;
  background: #1b92a6;
  color: #fff;
  border: none;
  border-radius: var(--rad-m);
  padding: 10px 16px;
  font-weight: 600;
  font-size: 0.875rem;
  font-family: var(--primary-font);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: opacity 0.15s;
}
.generate-btn:hover:not(:disabled) {
  opacity: 0.9;
}
.generate-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.clear-btn {
  background: transparent;
  border: 1px solid var(--idi-shade3-100);
  color: var(--txt-secondary-1);
  border-radius: var(--rad-m);
  padding: 10px 16px;
  font-weight: 600;
  font-size: 0.875rem;
  font-family: var(--primary-font);
  cursor: pointer;
}
.clear-btn:hover:not(:disabled) {
  background: var(--idi-shade3-50);
  color: var(--txt-primary);
}
.clear-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.empty-match {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 16px;
  border: 1px dashed var(--idi-shade3-50);
  border-radius: var(--rad-xl);
  color: var(--txt-secondary-1);
  font-size: 0.8125rem;
  text-align: center;
}

.match-panel {
  background: var(--idi-shade2-100);
  border: 1px solid var(--idi-shade3-50);
  border-radius: var(--rad-lg);
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.match-row {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 8px;
  align-items: baseline;
  font-size: 0.8125rem;
}
.match-label {
  color: var(--txt-secondary-2);
  text-transform: uppercase;
  font-size: 0.6875rem;
  letter-spacing: 0.04em;
  font-weight: 600;
}
.match-value {
  color: var(--txt-primary);
  word-break: break-all;
}
.match-value.mono {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 0.75rem;
  color: var(--txt-secondary-1);
}

.warn-banner {
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.3);
  color: #f59e0b;
  border-radius: var(--rad-m);
  padding: 10px 12px;
  font-size: 0.75rem;
  line-height: 1.5;
  margin-top: 12px;
}
.warn-banner.mt-0 {
  margin-top: 0;
}
.warn-banner code {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 0.6875rem;
}

.fields-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--idi-shade3-50);
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.fields-header {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.fields-title {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--txt-primary);
  margin: 0;
}
.fields-subtitle {
  font-size: 0.75rem;
  color: var(--txt-secondary-1);
  margin: 0;
}
.fields-subtitle code {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 0.6875rem;
  background: var(--idi-shade3-25);
  padding: 1px 4px;
  border-radius: 3px;
}

.error_message {
  color: #ef4444;
  font-size: 0.8125rem;
}

.select-label {
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--txt-secondary-2);
  font-weight: 600;
  margin-bottom: -4px;
}
.retry-link {
  background: none;
  border: none;
  color: #1b92a6;
  text-decoration: underline;
  font-size: 0.8125rem;
  cursor: pointer;
  padding: 0 0 0 4px;
  font-family: var(--primary-font);
}

.mt-4 {
  margin-top: 16px;
}
.mb-2 {
  margin-bottom: 8px;
}
</style>
